import React, { useState, useEffect, useRef, useCallback } from 'react';
import socketService from '../../services/sockect';
import {  uploadMultipleFiles } from '../../services/uploadService';
import { LabReport } from '../../components/doctor/labReport';
import { useLocation, useParams } from 'react-router-dom';
import Button from '../../components/elements/button';
import MapCom from '../../components/map' ;
import { 
    Check, Close, Send, Map, Lab, Chat, Upload, 
    Direction
} from '../../assets/icons';
import '../../assets/css/doctor/case.css';
import { messageApi } from '../../network/apis/message';
import { toast } from 'react-toastify';
import { casesApi } from '../../network/apis/cases';

const DoctorCase = () => {

    const { id, doctorId } = useParams();
    const messagesEndRef = useRef(null);
    const [, setIsAiTyping] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const location = useLocation();
    const unsubscribeRef = useRef(null);

    // Handle new socket messages
    const handleNewMessage = useCallback((message) => {
        setMessages(prev => {
            // Prevent duplicate messages
            const exists = prev.some(m => 
                m.id === message.id || 
                (m.content === message.content && m.created_at === message.created_at)
            );
            if (exists) return prev;
            return [...prev, message];
        });
        scrollToBottom();
    }, []);

    useEffect(() => {
        // Connect to socket with proper error handling
        const initSocket = async () => {
            try {
                await socketService.connect(doctorId);
                
                // Subscribe to case messages
                unsubscribeRef.current = socketService.subscribeToCase(id, handleNewMessage);
                
            } catch (error) {
                console.error('Failed to connect to socket:', error);
                toast.error('Real-time connection failed. Messages may be delayed.');
            }
        };

        initSocket();

        // Listen for connection state changes
        const unsubscribeConnection = socketService.onConnectionChange((connected, error) => {
            setIsConnected(connected);
            if (error) {
                console.error('Connection error:', error);
            }
        });

        // Cleanup on unmount
        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
            }
            unsubscribeConnection();
            // Don't disconnect completely - other components might need it
            // socketService.disconnect();
        };
    }, [id, doctorId, handleNewMessage]);

     useEffect(() => {
            fetchMessages();
            scrollToBottom();
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);
    
        const scrollToBottom = () => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        };
    
        const fetchMessages = async () => {
            // Fetch messages from API or local storage
            try {
                const response = await messageApi.getMessages({"chat_id": "case_"+caseData.case_code});
                if (response.data && response.data !== null) {
                    setMessages(response.data);
                } else {
                    setMessages([]);
                }
            } catch (error) {
                console.error('Error fetching messages:', error);
            } finally {
                // setLoading(false);
            }
        };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || isSending) return;

        setIsSending(true);
        const messageContent = newMessage;
        setNewMessage(''); // Clear input immediately for better UX

        try {
            // Check for AI trigger words
            const lowerMessage = messageContent.toLowerCase();
            if (lowerMessage.includes('@ai') || lowerMessage.includes('@aquabot')) {
                handleAiResponse(messageContent);
            }

            // Send via API (primary method)
            const response = await messageApi.newMessage({
                chat_id: "case_" + caseData.case_code,
                content: messageContent,
                attachments: []
            });

            if (response.data) {
                // Add message to local state
                setMessages(prev => [...prev, response.data]);
                scrollToBottom();

                // Also try to send via socket for real-time sync to other clients
                if (isConnected) {
                    try {
                        await socketService.sendMessage(id, {
                            content: messageContent,
                            sender_role: 'doctor',
                            ...response.data
                        });
                    } catch (socketError) {
                        // Socket send failed but API succeeded, so message is saved
                        console.warn('Socket send failed, but message saved via API:', socketError);
                    }
                }
            }
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message. Please try again.');
            // Restore message on failure
            setNewMessage(messageContent);
        } finally {
            setIsSending(false);
        }
    };

    // const handleKeyDown = (e) => {
    //     if (e.key === 'Enter' && !e.shiftKey) {
    //         e.preventDefault();
    //         handleSendMessage();
    //     }
    // };

    const handleAiResponse = (userMessage) => {
        // Show typing indicator
        setIsAiTyping(true);
        
        // Simulate AI processing time
        setTimeout(() => {
            let response;
            const lowerMessage = userMessage.toLowerCase();
            
            if (lowerMessage.includes('water quality')) {
                response = "Based on your water parameters, I'd recommend testing ammonia and nitrite levels. The current pH of 7.2 is within acceptable range for most fish species, but the elevated mortality rate might indicate water quality issues.";
            } else if (lowerMessage.includes('treatment') || lowerMessage.includes('medicine')) {
                response = "Before recommending treatments, Dr. Johnson would need more information. Could you share more details about symptoms? In the meantime, ensure good water circulation and consider reducing feeding until diagnosis is confirmed.";
            } else if (lowerMessage.includes('visit') || lowerMessage.includes('appointment')) {
                response = "I've notified Dr. Johnson about your request for a farm visit. You can schedule one using the 'Request Farm Visit' button below.";
            } else {
                response = "I've analyzed your case details. The combination of erratic swimming and loss of appetite could indicate several issues like parasitic infection or water quality problems. Dr. Johnson will be able to provide a more specific diagnosis soon.";
            }
            
            const aiMessage = {
                sender: 'bot',
                senderName: 'AquaBot',
                time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
                content: response,
                attachments: []
            };
            
            setMessages(prev => [...prev, aiMessage]);
            setIsAiTyping(false);
        }, 2000);
    };

    const handleFileUpload = async (files) => {
       await uploadMultipleFiles(
            files,
            `cases/${id}/attachments`
        );
        // Handle the uploaded files
    };

    const handleLabReportSubmit = async () => {
        // Handle lab report submission
    };

    // Add these state variables to your component
const [newPrescription, setNewPrescription] = useState({
    diagnosis: '',
    clinicalObservation: '',
    medications: [],
    waterTreatment: '',
    feeding: '',
    followUp: '',
    isDraft: false
});

const [savedPrescriptions, setSavedPrescriptions] = useState([]);
const [viewingPrescription, setViewingPrescription] = useState(null);

// Add these functions to handle prescriptions
const handleAddMedication = () => {
    setNewPrescription({
        ...newPrescription,
        medications: [
            ...newPrescription.medications, 
            { name: '', dosage: '', frequency: '', instructions: '' }
        ]
    });
};

const handleRemoveMedication = (index) => {
    const updatedMeds = [...newPrescription.medications];
    updatedMeds.splice(index, 1);
    setNewPrescription({
        ...newPrescription,
        medications: updatedMeds
    });
};

const handleMedicationChange = (index, field, value) => {
    const updatedMeds = [...newPrescription.medications];
    updatedMeds[index] = { ...updatedMeds[index], [field]: value };
    setNewPrescription({
        ...newPrescription,
        medications: updatedMeds
    });
};

const handleSavePrescription = async (isDraft) => {
    try {
        // Validate form
        if (!newPrescription.diagnosis.trim()) {
            toast.error('Please enter a diagnosis');
            return;
        }
        
        if (newPrescription.medications.length === 0) {
            toast.error('Please add at least one medication');
            return;
        }
        
        // Check if all medications have at least a name and dosage
        const invalidMeds = newPrescription.medications.filter(med => 
            !med.name.trim() || !med.dosage.trim()
        );
        
        if (invalidMeds.length > 0) {
            toast.error('Please complete all medication entries with at least a name and dosage');
            return;
        }
        
        // Create prescription object
        const prescriptionToSave = {
            ...newPrescription,
            id: `RX-${Date.now().toString().slice(-6)}`,
            caseId: id,
            createdAt: new Date().toISOString(),
            isDraft
        };
        
        // In a real app, you would send this to your API
        // const response = await prescriptionApi.createPrescription(prescriptionToSave);
        
        // For now, we'll just update the local state
        setSavedPrescriptions([...savedPrescriptions, prescriptionToSave]);
        
        // Clear the form
        setNewPrescription({
            diagnosis: '',
            clinicalObservation: '',
            medications: [],
            waterTreatment: '',
            feeding: '',
            followUp: '',
            isDraft: false
        });
        
        toast.success(isDraft ? 'Prescription saved as draft' : 'Prescription issued successfully');
    } catch (error) {
        console.error('Error saving prescription:', error);
        toast.error('Failed to save prescription. Please try again.');
    }
};

const handleEditPrescription = (prescription) => {
    setNewPrescription(prescription);
    // Remove from saved prescriptions since we're editing it
    setSavedPrescriptions(savedPrescriptions.filter(p => p.id !== prescription.id));
};

const handleViewPrescription = (prescription) => {
    setViewingPrescription(prescription);
};

const handleDownloadPrescription = (_prescription) => {
    // In a real app, you would generate a PDF here
    // For now, we'll just show a success message
    toast.info('Downloading prescription as PDF...');
    
    // Simulate download delay
    setTimeout(() => {
        toast.success('Prescription downloaded successfully');
    }, 2000);
};

    // const [caseData] = useState({
    //     id: 'CS001',
    //     status: 'pending',
    //     title: 'Abnormal Fish Behavior',
    //     farm: {
    //         name: 'Atlantic Fish Farm',
    //         location: {
    //             address: 'Plot 123, Aquaculture Zone, Lagos',
    //             coordinates: {
    //                 lat: 6.5244,
    //                 lng: 3.3792
    //             }
    //         }
    //     },
    //     reportedDate: '2024-03-15',
    //     description: 'Fish showing erratic swimming patterns and loss of appetite',
    //     symptoms: ['Erratic swimming', 'Loss of appetite', 'Discoloration'],
    //     waterParameters: {
    //         temperature: '28°C',
    //         pH: '7.2',
    //         oxygen: '6.5 mg/L',
    //         ammonia: '0.02 mg/L'
    //     },
    //     images: ['image1.jpg', 'image2.jpg'],
    //     priority: 'high',
    //     appointment: {
    //         requested: true,
    //         date: '2024-03-20',
    //         time: '10:00 AM',
    //         status: 'pending'
    //     }
    // });

    // Get initial case data from navigation state, but fetch fresh data from API
    const [caseData, setCaseData] = useState(location.state?.case || null);
    const [isLoadingCase, setIsLoadingCase] = useState(!location.state?.case);

    // Fetch case details from API
    useEffect(() => {
        const fetchCaseDetails = async () => {
            try {
                setIsLoadingCase(true);
                const response = await casesApi.getCase(id);
                if (response.data) {
                    setCaseData(response.data);
                }
            } catch (error) {
                console.error('Error fetching case details:', error);
                if (!caseData) {
                    toast.error('Failed to load case details');
                }
            } finally {
                setIsLoadingCase(false);
            }
        };

        // Always fetch fresh data
        fetchCaseDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const [activeTab, setActiveTab] = useState('chat');

    const [showMap, setShowMap] = useState(false);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');

    // Helper to get farm location display
    const getFarmLocation = () => {
        if (caseData?.farm?.city && caseData?.farm?.state) {
            return `${caseData.farm.city}, ${caseData.farm.state}`;
        }
        if (caseData?.farm?.address) {
            return caseData.farm.address;
        }
        if (caseData?.farm?.location) {
            return caseData.farm.location;
        }
        return 'Location not available';
    };

    // Check if map coordinates are available
    const hasMapCoordinates = () => {
        return (caseData?.farm?.latitude && caseData?.farm?.longitude) || 
               (caseData?.farm?.location?.coordinates?.lat && caseData?.farm?.location?.coordinates?.lng);
    };

    // Get map coordinates
    const getMapCoordinates = () => {
        if (caseData?.farm?.latitude && caseData?.farm?.longitude) {
            return { lat: caseData.farm.latitude, lng: caseData.farm.longitude };
        }
        if (caseData?.farm?.location?.coordinates) {
            return caseData.farm.location.coordinates;
        }
        return null;
    };

    const handleApproveCase = async () => {
        try {
            const response = await casesApi.acceptCase(caseData.id);
            if (response.status === 200) {
                toast.success('Case approved successfully!');
                setCaseData(prev => ({ ...prev, status: 'approved' }));
            } else {
                toast.error('Failed to approve case. Please try again.');
            }
        } catch (error) {
            console.error('Error approving case:', error);
            toast.error('Failed to approve case. Please try again.');
        }
    };

    const handleDeclineCase = async () => {
        try {
            const response = await casesApi.rejectCase(caseData.id);
            if (response.status === 200) {
                toast.success('Case declined successfully!');
                setCaseData(prev => ({ ...prev, status: 'rejected' }));
            } else {
                toast.error('Failed to decline case. Please try again.');
            }
        } catch (error) {
            console.error('Error declining case:', error);
            toast.error('Failed to decline case. Please try again.');
        }
    };

    // Loading state
    if (isLoadingCase && !caseData) {
        return (
            <div className="doctor-case loading">
                <div className="loading-spinner">Loading case details...</div>
            </div>
        );
    }

    // No case data
    if (!caseData) {
        return (
            <div className="doctor-case error">
                <div className="error-message">
                    <h2>Case not found</h2>
                    <p>The case you're looking for doesn't exist or you don't have access to it.</p>
                </div>
            </div>
        );
    }

    // const handleApproveAppointment = () => {
    //     // Handle appointment approval
    // };

    return (
        <div className="doctor-case">
            <div className="case-header">
            <div className="case-title">
    <h1>{caseData.title}</h1>
    <span className={`status ${caseData.status?.toLowerCase()}`}>
        {caseData.status?.toLowerCase()}
    </span>
</div>

                {caseData.status.toLowerCase() === 'pending' && (
                    <div className="approval-actions">
                        <Button
                            icon={<img src={Close} alt="" />}
                            text="Decline Case"
                            variant="outline"
                            onClick={handleDeclineCase}
                        />
                        <Button
                            icon={<img src={Check} alt="" />}
                            text="Accept Case"
                            variant="primary"
                            onClick={handleApproveCase}
                        />
                    </div>
                )}
            </div>

            <div className="case-layout">
                <div className="case-details">
                    <section className="farm-info">
                        <h2>Farm Information</h2>
                        <div className="farm-card">
                            <h3>{caseData.farm?.name || 'Farm Name Not Available'}</h3>
                            <p className="location">
                                <img src={Map} alt="" />
                                {getFarmLocation()}
                            </p>
                            {hasMapCoordinates() && (
                                <div className="location-actions">
                                    <Button
                                        icon={<img src={Map} alt="" />}
                                        text="View Map"
                                        variant="outline"
                                        onClick={() => setShowMap(true)}
                                    />
                                    <Button
                                        icon={<img src={Direction} alt="" />}
                                        text="Get Directions"
                                        variant="outline"
                                        onClick={() => {
                                            const coords = getMapCoordinates();
                                            if (coords) {
                                                window.open(`https://www.google.com/maps/dir/?api=1&destination=${coords.lat},${coords.lng}`);
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </section>

                    <section className="case-symptoms">
                        <h2>Reported Symptoms</h2>
                        <div className="symptoms-list">
                            {caseData.symptoms && caseData.symptoms.length > 0 ? (
                                caseData.symptoms.map((symptom, index) => (
                                    <span key={index} className="symptom-tag">
                                        {symptom}
                                    </span>
                                ))
                            ) : (
                                <p className="no-data">No symptoms reported</p>
                            )}
                        </div>
                    </section>

                    {/* <section className="water-parameters">
                        <h2>Water Parameters</h2>
                        <div className="parameters-grid">
                            {Object.entries(caseData.waterParameters).map(([key, value]) => (
                                <div key={key} className="parameter-card">
                                    <label>{key}</label>
                                    <span>{value}</span>
                                </div>
                            ))}
                        </div>
                    </section> */}

                    <section className="case-images">
                        <h2>Uploaded Images</h2>
                        {/* <div className="images-grid">
                            {caseData.images.map((image, index) => (
                                <img key={index} src={image} alt={`Case image ${index + 1}`} />
                            ))}
                        </div> */}
                    </section>

                    {/* {caseData.appointment.requested && (
                        <section className="appointment-request">
                            <h2>Appointment Request</h2>
                            <div className="appointment-card">
                                <div className="appointment-details">
                                    <img src={Calendar} />
                                    <p>Requested for {caseData.appointment.date} at {caseData.appointment.time}</p>
                                </div>
                                {caseData.appointment.status === 'pending' && (
                                    <div className="appointment-actions">
                                        <Button
                                            text="Approve"
                                            variant="success"
                                            onClick={handleApproveAppointment}
                                        />
                                        <Button
                                            text="Suggest New Time"
                                            variant="outline"
                                        />
                                    </div>
                                )}
                            </div>
                        </section>
                    )} */}
                </div>

                <div className="case-communication">
                <div className="communication-tabs">
    <button 
        className={activeTab === 'chat' ? 'active' : ''} 
        onClick={() => setActiveTab('chat')}
    >
        Chat
    </button>
    <button 
        className={activeTab === 'lab' ? 'active' : ''} 
        onClick={() => setActiveTab('lab')}
    >
        Lab Reports
    </button>
    <button 
        className={activeTab === 'prescriptions' ? 'active' : ''} 
        onClick={() => setActiveTab('prescriptions')}
    >
        Prescriptions
    </button>
</div>

{activeTab === 'chat' && (
    <div className="chat-container">
        <div className="chat-header">
            <span className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
                <span className="status-dot"></span>
                {isConnected ? 'Connected' : 'Reconnecting...'}
            </span>
        </div>
                        <div className="messages">
    {messages.length === 0 ? (
        <div className="no-messages">
            <img src={Chat} alt="No messages" />
            <p>No messages yet. Start the conversation with the farm owner.</p>
        </div>
    ) : (
        messages.map((message, index) => (
            <div key={index} className={`message ${message.sender_role.toLowerCase()}`}>
                <div className="message-content">
                    <p>{message.content}</p>
                    {message.attachments && message.attachments.length > 0 && (
                        <div className="attachments">
                            {message.attachments.map((file, i) => (
                                <div key={i} className="attachment">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                                        <polyline points="13 2 13 9 20 9"></polyline>
                                    </svg>
                                    <span>{file.name}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <span className="time">
                    {new Date(message.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </span>
            </div>
        ))
    )}
</div>
{/* {caseData.status?.toLowerCase() === 'approved' && ( */}
    <div className="message-input">
        <textarea
            placeholder="Type your message to the farm owner..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                }
            }}
        />
        <div className="input-actions">
            <Button 
                icon={<img src={Upload} alt="Upload" />} 
                variant="outline" 
                title="Upload Files"
                onClick={() => document.getElementById('file-upload').click()}
            />
            <input 
                id="file-upload" 
                type="file" 
                multiple 
                style={{display: 'none'}} 
                onChange={(e) => handleFileUpload(e.target.files)}
            />
            <Button 
                icon={<img src={Lab} alt="Lab" />} 
                variant="outline" 
                title="Create Lab Report"
                onClick={() => setActiveTab('lab')}
            />
            <Button 
                icon={<img src={Send} alt="Send" />} 
                text={isSending ? "Sending..." : "Send"}
                variant="primary" 
                disabled={!newMessage.trim() || isSending}
                loading={isSending}
                onClick={handleSendMessage}
            />
        </div>
    </div>
{/* )} */}
                    </div>
                )}

{activeTab === 'lab' && (
    <div className="lab-reports">
        <LabReport caseId={id} onSubmit={handleLabReportSubmit} />
    </div>
)}

{activeTab === 'prescriptions' && (
    <div className="prescriptions">
        <div className="prescription-form">
            <h3>Create New Prescription</h3>
            <div className="prescription-fields">
                <div className="field-group">
                    <label htmlFor="diagnosis">Diagnosis</label>
                    <textarea 
                        id="diagnosis" 
                        value={newPrescription.diagnosis} 
                        onChange={(e) => setNewPrescription({...newPrescription, diagnosis: e.target.value})}
                        placeholder="Enter your diagnosis based on examination and test results"
                    />
                </div>
                
                <div className="field-group">
                    <label htmlFor="clinicalObservation">Clinical Observations</label>
                    <textarea 
                        id="clinicalObservation" 
                        value={newPrescription.clinicalObservation} 
                        onChange={(e) => setNewPrescription({...newPrescription, clinicalObservation: e.target.value})}
                        placeholder="Describe your observations of the fish/aquatic species condition"
                    />
                </div>
                
                <div className="medications-section">
                    <div className="section-header">
                        <h4>Medications</h4>
                        <button 
                            type="button" 
                            className="add-medication-btn"
                            onClick={handleAddMedication}
                        >
                            + Add Medication
                        </button>
                    </div>
                    
                    {newPrescription.medications.map((med, index) => (
                        <div key={index} className="medication-item">
                            <div className="medication-row">
                                <div className="med-field">
                                    <label>Name</label>
                                    <input 
                                        type="text" 
                                        value={med.name}
                                        onChange={(e) => handleMedicationChange(index, 'name', e.target.value)}
                                        placeholder="Medication name"
                                    />
                                </div>
                                <div className="med-field">
                                    <label>Dosage</label>
                                    <input 
                                        type="text" 
                                        value={med.dosage}
                                        onChange={(e) => handleMedicationChange(index, 'dosage', e.target.value)}
                                        placeholder="e.g., 10mg/L"
                                    />
                                </div>
                                <div className="med-field">
                                    <label>Frequency</label>
                                    <input 
                                        type="text" 
                                        value={med.frequency}
                                        onChange={(e) => handleMedicationChange(index, 'frequency', e.target.value)}
                                        placeholder="e.g., Twice daily"
                                    />
                                </div>
                                <button 
                                    type="button" 
                                    className="remove-med-btn"
                                    onClick={() => handleRemoveMedication(index)}
                                >
                                    ×
                                </button>
                            </div>
                            <div className="med-field full-width">
                                <label>Instructions</label>
                                <textarea 
                                    value={med.instructions}
                                    onChange={(e) => handleMedicationChange(index, 'instructions', e.target.value)}
                                    placeholder="Special instructions for this medication"
                                />
                            </div>
                        </div>
                    ))}
                </div>
                
                <div className="field-group">
                    <label htmlFor="waterTreatment">Water Treatment Recommendations</label>
                    <textarea 
                        id="waterTreatment" 
                        value={newPrescription.waterTreatment} 
                        onChange={(e) => setNewPrescription({...newPrescription, waterTreatment: e.target.value})}
                        placeholder="Recommendations for water quality management and treatment"
                    />
                </div>
                
                <div className="field-group">
                    <label htmlFor="feeding">Feeding Recommendations</label>
                    <textarea 
                        id="feeding" 
                        value={newPrescription.feeding} 
                        onChange={(e) => setNewPrescription({...newPrescription, feeding: e.target.value})}
                        placeholder="Diet and feeding recommendations"
                    />
                </div>
                
                <div className="field-group">
                    <label htmlFor="followUp">Follow-Up Instructions</label>
                    <textarea 
                        id="followUp" 
                        value={newPrescription.followUp} 
                        onChange={(e) => setNewPrescription({...newPrescription, followUp: e.target.value})}
                        placeholder="Follow-up schedule and monitoring instructions"
                    />
                </div>
            </div>
            
            <div className="prescription-actions">
                <Button 
                    text="Save Draft" 
                    variant="outline"
                    onClick={() => handleSavePrescription(true)}
                />
                <Button 
                    text="Issue Prescription" 
                    variant="primary"
                    onClick={() => handleSavePrescription(false)}
                />
            </div>
        </div>
        
        {savedPrescriptions.length > 0 && (
            <div className="saved-prescriptions">
                <h3>Issued Prescriptions</h3>
                <div className="prescription-list">
                    {savedPrescriptions.map((prescription, index) => (
                        <div key={index} className="prescription-card">
                            <div className="prescription-header">
                                <div className="prescription-info">
                                    <h4>Prescription #{prescription.id}</h4>
                                    <span className="prescription-date">
                                        {new Date(prescription.createdAt).toLocaleDateString('en-US', {
                                            year: 'numeric', 
                                            month: 'short', 
                                            day: 'numeric'
                                        })}
                                    </span>
                                </div>
                                <div className="prescription-status">
                                    {prescription.isDraft ? 
                                        <span className="status-badge draft">Draft</span> : 
                                        <span className="status-badge issued">Issued</span>
                                    }
                                </div>
                            </div>
                            
                            <div className="prescription-details">
                                <div className="detail-section">
                                    <h5>Diagnosis</h5>
                                    <p>{prescription.diagnosis}</p>
                                </div>
                                
                                <div className="detail-section">
                                    <h5>Medications ({prescription.medications.length})</h5>
                                    <ul className="medications-list">
                                        {prescription.medications.map((med, medIndex) => (
                                            <li key={medIndex}>
                                                <strong>{med.name}</strong> - {med.dosage}, {med.frequency}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            
                            <div className="prescription-actions">
                                <button 
                                    className="view-details-btn"
                                    onClick={() => handleViewPrescription(prescription)}
                                >
                                    View Details
                                </button>
                                {prescription.isDraft && (
                                    <button 
                                        className="edit-btn"
                                        onClick={() => handleEditPrescription(prescription)}
                                    >
                                        Edit Draft
                                    </button>
                                )}
                                <button 
                                    className="download-btn"
                                    onClick={() => handleDownloadPrescription(prescription)}
                                >
                                    Download PDF
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}
        
        {viewingPrescription && (
            <div className="modal-overlay">
                <div className="modal-content prescription-detail-modal">
                    <div className="modal-header">
                        <h2>Prescription Details</h2>
                        <button 
                            className="close-modal-btn"
                            onClick={() => setViewingPrescription(null)}
                        >
                            ×
                        </button>
                    </div>
                    
                    <div className="prescription-detail-content">
                        <div className="prescription-detail-header">
                            <div>
                                <h3>Prescription #{viewingPrescription.id}</h3>
                                <span className="prescription-date">
                                    {new Date(viewingPrescription.createdAt).toLocaleDateString('en-US', {
                                        year: 'numeric', 
                                        month: 'long', 
                                        day: 'numeric'
                                    })}
                                </span>
                            </div>
                            <div className="prescription-status">
                                {viewingPrescription.isDraft ? 
                                    <span className="status-badge draft">Draft</span> : 
                                    <span className="status-badge issued">Issued</span>
                                }
                            </div>
                        </div>
                        
                        <div className="detail-section">
                            <h4>Diagnosis</h4>
                            <p>{viewingPrescription.diagnosis}</p>
                        </div>
                        
                        <div className="detail-section">
                            <h4>Clinical Observations</h4>
                            <p>{viewingPrescription.clinicalObservation}</p>
                        </div>
                        
                        <div className="detail-section">
                            <h4>Medications</h4>
                            <div className="medications-detail">
                                {viewingPrescription.medications.map((med, index) => (
                                    <div key={index} className="medication-detail-item">
                                        <h5>{med.name}</h5>
                                        <div className="med-details-grid">
                                            <div>
                                                <span className="med-label">Dosage:</span>
                                                <span>{med.dosage}</span>
                                            </div>
                                            <div>
                                                <span className="med-label">Frequency:</span>
                                                <span>{med.frequency}</span>
                                            </div>
                                        </div>
                                        <div className="med-instructions">
                                            <span className="med-label">Instructions:</span>
                                            <p>{med.instructions}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <div className="detail-section">
                            <h4>Water Treatment Recommendations</h4>
                            <p>{viewingPrescription.waterTreatment}</p>
                        </div>
                        
                        <div className="detail-section">
                            <h4>Feeding Recommendations</h4>
                            <p>{viewingPrescription.feeding}</p>
                        </div>
                        
                        <div className="detail-section">
                            <h4>Follow-Up Instructions</h4>
                            <p>{viewingPrescription.followUp}</p>
                        </div>
                    </div>
                    
                    <div className="modal-actions">
                        <Button 
                            text="Download PDF" 
                            variant="outline"
                            onClick={() => handleDownloadPrescription(viewingPrescription)}
                        />
                        <Button 
                            text="Close" 
                            variant="primary"
                            onClick={() => setViewingPrescription(null)}
                        />
                    </div>
                </div>
            </div>
        )}
    </div>
)}
                </div>
            </div>

            {showMap && hasMapCoordinates() && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <MapCom 
                            center={getMapCoordinates()}
                            zoom={15}
                        />
                        <Button
                            text="Close"
                            onClick={() => setShowMap(false)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default DoctorCase;